/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/* eslint @coze-arch/max-line-per-function: ["error", {"max": 600}] */
/* eslint-disable complexity */
import { useNavigate } from 'react-router-dom';
import { type FC, useCallback, useEffect, useState } from 'react';

import classNames from 'classnames';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  highlightFilterStyle,
  WorkspaceEmpty,
  DevelopCustomPublishStatus,
  DevelopCustomTypeStatus,
  isPublishStatus,
  isSearchScopeEnum,
  getPublishRequestParam,
  getTypeRequestParams,
  isEqualDefaultFilterParams,
  isFilterHighlight,
  CREATOR_FILTER_OPTIONS,
  FILTER_PARAMS_DEFAULT,
  STATUS_FILTER_OPTIONS,
  TYPE_FILTER_OPTIONS_WITHOUT_PROJECT,
  useIntelligenceList,
  useIntelligenceActions,
  useCachedQueryParams,
  useGlobalEventListeners,
  type DevelopProps,
  useProjectCopyPolling,
  useCardActions,
} from '@coze-studio/workspace-base/develop';
import { setWorkspaceRoutePrefix } from '@coze-studio/abr-brand';
import { useSpaceStore } from '@coze-foundation/space-store-adapter';
import {
  type IntelligenceData,
  IntelligenceType,
  search,
  SearchScope,
} from '@coze-arch/idl/intelligence_api';
import { I18n } from '@coze-arch/i18n';
import { IconCozLoading } from '@coze-arch/coze-design/icons';
import { IconButton, Search, Select, Spin } from '@coze-arch/coze-design';
import { EVENT_NAMES, sendTeaEvent } from '@coze-arch/bot-tea';
import { SpaceType } from '@coze-arch/bot-api/developer_api';

import {
  navigateToIntelligence,
  useQuickOpen,
} from '../../hooks/use-quick-open';
import { usePublishedAppsCount } from '../../hooks/use-published-apps-count';
import { useOntologyAppsCount } from '../../hooks/use-ontology-apps-count';
import { useLibraryResourceMetrics } from '../../hooks/use-library-resources-count';
import { useDevelopMetrics } from '../../hooks/use-develop-metrics';
import { QuickOpenPalette } from '../../components/quick-open-palette';
import { HeroDashboard } from '../../components/hero-dashboard';
import { DevelopTable } from '../../components/develop-table';
import { BotCardTech } from '../../components/bot-card-tech';

import '../../theme/abr-home-theme-tech.less';
import styles from './index.module.less';

const abrHomeQueryClient = new QueryClient();

type ViewMode = 'card' | 'list';

const DevelopV2Inner: FC<DevelopProps> = ({ spaceId }) => {
  const navigate = useNavigate();
  const { open, shortcutLabel, openPalette, closePalette } = useQuickOpen();

  const isPersonal = useSpaceStore(
    state => state.space.space_type === SpaceType.Personal,
  );
  const spaceName = useSpaceStore(state => state.space.name);

  const [viewMode, setViewMode] = useState<ViewMode>('card');

  const [filterParams, setFilterParams, debouncedSetSearchValue] =
    useCachedQueryParams();

  const {
    isIntelligenceTypeFilterHighlight,
    isOwnerFilterHighlight,
    isPublishAndOpenFilterHighlight,
  } = isFilterHighlight(filterParams);

  const {
    listResp: { loading, data, loadingMore, mutate, noMore, reload },
    containerRef,
  } = useIntelligenceList({
    params: {
      spaceId,
      searchValue: filterParams.searchValue,
      types: getTypeRequestParams({ type: filterParams.searchType }),
      hasPublished: getPublishRequestParam(filterParams.isPublish),
      searchScope: filterParams.searchScope,
      orderBy:
        filterParams.isPublish === DevelopCustomPublishStatus.Publish
          ? search.OrderBy.PublishTime
          : search.OrderBy.UpdateTime,
    },
  });

  const { count: ontologyAppCount } = useOntologyAppsCount();
  const libraryResources = useLibraryResourceMetrics(spaceId);
  const { count: publishedAppCount, loading: publishedAppLoading } =
    usePublishedAppsCount();
  const metrics = useDevelopMetrics({
    list: data?.list,
    ontologyAppCount,
    publishedAppCount,
    publishedAppLoading,
    libraryResources,
  });

  useGlobalEventListeners({ reload, spaceId });

  useEffect(() => {
    setWorkspaceRoutePrefix('');
  }, []);

  useEffect(() => {
    setFilterParams(prev => ({ ...prev, searchValue: '' }));
  }, [spaceId, setFilterParams]);

  useEffect(() => {
    if (filterParams.searchType === DevelopCustomTypeStatus.Project) {
      setFilterParams(prev => ({
        ...prev,
        searchType: DevelopCustomTypeStatus.All,
      }));
    }
  }, [filterParams.searchType, setFilterParams]);

  useEffect(() => {
    sendTeaEvent(EVENT_NAMES.view_bot, { tab: 'my_bots' });
  }, []);

  useProjectCopyPolling({
    listData: data?.list,
    spaceId,
    mutate,
  });

  const { contextHolder: cardActionsContextHolder, actions: cardActions } =
    useCardActions({
      isPersonalSpace: isPersonal,
      mutate,
    });

  const { contextHolder, actions } = useIntelligenceActions({
    spaceId,
    mutateList: mutate,
    reloadList: reload,
    skipGuideModal: true,
  });

  const handleQuickOpenSelect = useCallback(
    (item: IntelligenceData) => {
      navigateToIntelligence(navigate, item);
      closePalette();
    },
    [closePalette, navigate],
  );

  return (
    <>
      {contextHolder}
      {cardActionsContextHolder}
      <div className={classNames('abr-tech-page', styles.page)}>
        <HeroDashboard
          spaceId={spaceId}
          spaceName={spaceName}
          metrics={metrics}
          shortcutLabel={shortcutLabel}
          onQuickOpen={openPalette}
          onCreateAgent={actions.createIntelligence}
        />
        <QuickOpenPalette
          open={open}
          shortcutLabel={shortcutLabel}
          items={data?.list ?? []}
          onClose={closePalette}
          onSelect={handleQuickOpenSelect}
        />
        <div className={classNames('abr-tech-content-section', styles.content)}>
          <div className={styles.contentHeader}>
            <span className={styles.sectionTitle}>
              {I18n.t('abr_home_resource_pool')}
            </span>
          </div>
          <div className={styles.toolbar}>
            <Search
              showClear={true}
              className="w-[200px]"
              style={filterParams.searchValue ? highlightFilterStyle : {}}
              placeholder={I18n.t('workspace_develop_search_project')}
              value={filterParams.searchValue}
              onChange={val => debouncedSetSearchValue(val)}
            />
            <Select
              className="min-w-[128px]"
              style={
                isIntelligenceTypeFilterHighlight ? highlightFilterStyle : {}
              }
              value={filterParams.searchType}
              onChange={val => {
                setFilterParams(prev => ({
                  ...prev,
                  searchType:
                    val as (typeof TYPE_FILTER_OPTIONS_WITHOUT_PROJECT)[number]['value'],
                }));
              }}
            >
              {TYPE_FILTER_OPTIONS_WITHOUT_PROJECT.map(opt => (
                <Select.Option key={opt.value} value={opt.value}>
                  {I18n.t(opt.labelI18NKey)}
                </Select.Option>
              ))}
            </Select>
            {!isPersonal ? (
              <Select
                className="min-w-[128px]"
                style={isOwnerFilterHighlight ? highlightFilterStyle : {}}
                value={filterParams.searchScope}
                onChange={val => {
                  if (!isSearchScopeEnum(val)) {
                    return;
                  }
                  setFilterParams(p => ({ ...p, searchScope: val }));
                }}
              >
                {CREATOR_FILTER_OPTIONS.map(opt => (
                  <Select.Option key={opt.value} value={opt.value}>
                    {I18n.t(opt.labelI18NKey)}
                  </Select.Option>
                ))}
              </Select>
            ) : null}
            <Select
              className="min-w-[128px]"
              style={
                isPublishAndOpenFilterHighlight ? highlightFilterStyle : {}
              }
              value={filterParams.isPublish}
              onChange={val => {
                if (!isPublishStatus(val)) {
                  return;
                }
                setFilterParams(p => ({
                  ...p,
                  searchScope: SearchScope.All,
                  isPublish: val,
                }));
              }}
            >
              {STATUS_FILTER_OPTIONS.map(opt => (
                <Select.Option key={opt.value} value={opt.value}>
                  {I18n.t(opt.labelI18NKey)}
                </Select.Option>
              ))}
            </Select>
            <div className={styles.viewToggle}>
              <button
                type="button"
                className={viewMode === 'list' ? styles.viewOn : undefined}
                onClick={() => setViewMode('list')}
              >
                {I18n.t('abr_home_view_list')}
              </button>
              <button
                type="button"
                className={viewMode === 'card' ? styles.viewOn : undefined}
                onClick={() => setViewMode('card')}
              >
                {I18n.t('abr_home_view_card')}
              </button>
            </div>
          </div>
          <div ref={containerRef}>
            <Spin spinning={loading} wrapperClassName="w-full !min-h-[40vh]">
              {data?.list.length ? (
                viewMode === 'card' ? (
                  <div className={styles.cardGrid}>
                    {data.list.map((project, index) => (
                      <BotCardTech
                        key={`${project.basic_info?.id}-${index}`}
                        intelligenceInfo={project}
                        onRetryCopy={cardActions.onRetryCopy}
                        onCancelCopyAfterFailed={
                          cardActions.onCancelCopyAfterFailed
                        }
                        onClick={() => cardActions.onClick(project)}
                        onUpdateIntelligenceInfo={cardActions.onUpdate}
                        onDelete={({ name, id, type }) => {
                          if (type === IntelligenceType.Bot) {
                            actions.deleteIntelligence({
                              name,
                              spaceId,
                              agentId: id,
                            });
                            return;
                          }
                          if (type === IntelligenceType.Project) {
                            actions.deleteIntelligence({ name, projectId: id });
                          }
                        }}
                        onCopyAgent={cardActions.onCopyAgent}
                        onCopyProject={params => {
                          cardActions.onCopyProject({
                            initialValue: {
                              project_id: params.id ?? '',
                              to_space_id: spaceId,
                              name: params.name ?? '',
                              description: params.description,
                              icon_uri: [
                                {
                                  uid: params.icon_uri,
                                  url: params.icon_url ?? '',
                                },
                              ],
                            },
                          });
                        }}
                        timePrefixType={
                          filterParams.isPublish ===
                          DevelopCustomPublishStatus.Publish
                            ? 'publish'
                            : 'edit'
                        }
                      />
                    ))}
                  </div>
                ) : (
                  <DevelopTable
                    list={data.list}
                    onRowClick={item => {
                      cardActions.onClick(item);
                      navigateToIntelligence(navigate, item);
                    }}
                  />
                )
              ) : null}
              {!data?.list?.length && !loading ? (
                <WorkspaceEmpty
                  onClear={() => setFilterParams(FILTER_PARAMS_DEFAULT)}
                  hasFilter={!isEqualDefaultFilterParams({ filterParams })}
                />
              ) : null}
              {data?.list.length && loadingMore ? (
                <div className={styles.loadingMore}>
                  <IconButton
                    icon={<IconCozLoading />}
                    loading
                    color="secondary"
                  />
                  <div>{I18n.t('Loading')}...</div>
                </div>
              ) : null}
              {noMore && data?.list.length ? (
                <div className={styles.listFooter} />
              ) : null}
            </Spin>
          </div>
        </div>
      </div>
    </>
  );
};

export const DevelopV2Page: FC<DevelopProps> = props => (
  <QueryClientProvider client={abrHomeQueryClient}>
    <DevelopV2Inner {...props} />
  </QueryClientProvider>
);

export default DevelopV2Page;
